import { BACKEND_URL, STRIPE_PUBLISHABLE_KEY } from '@/config';
﻿import React, { useState, useEffect, useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import '@/styles/propose-page.css';
import CurrencySelector from '@/components/currency/CurrencySelector';
import OnboardingModal from '@/components/OnboardingModal';
import VerificationGate from '@/components/verification/VerificationGate';
import CommissionPreview from '@/components/CommissionPreview';
import SenderFeeSummary from '@/components/SenderFeeSummary';
import VerificationBadges from '@/components/VerificationBadges';
import { MemberAvatar } from '@/components/design';
import { ArrowLeft, ArrowLeftRight, Check, Flag, Info, Lock, Mail, Package, Plane, Send, Zap } from 'lucide-react';
import '@/styles/annonce-page.css';
import { canDo } from '@/utils/verifications';
import { ROLE_LABELS, roleRequestPath } from '@/utils/roles';
import { loadStripeJs } from '@/utils/stripeJs';
import {
  CONSTRAINT_META, CARRIER_PREF_META, CONDITION_LABELS, TRANSPORT_LABELS, ccySym,
} from '@/utils/listingParsers';

const fmtAmt = (n) => Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORY_LABELS = {
  'Vêtements': 'Vêtements', 'Électronique': 'Électronique', 'Alimentaire': 'Alimentaire', 'Documents': 'Documents',
  'Médicaments': 'Médicaments', 'Cosmétiques': 'Cosmétiques', 'Livres': 'Livres', 'Textile': 'Textile', 'Autre': 'Autre',
};
const FLEX_LABELS = { pm3j: '± 3 jours', pm1s: '± 1 semaine', pm2s: '± 2 semaines', souple: '± 3 jours', libre: 'Flexible' };
// Libellés partagés écrits avec un emoji en tête (« ✈️ Avion ») : affichés ici sans lui (icônes Lucide seulement)
// Poids au format français (« 1,2 kg », jamais « 1.2 kg »)
const kg = (n) => `${Number(n).toLocaleString('fr-FR')} kg`;
const sansEmoji = (label) => String(label || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
import {
  SUPPORTED_CURRENCIES, ccySym as ccySymCurrency, ccyStripeOk,
  getCommonCurrencies, normalizeCurrencies,
} from '@/utils/currencyUtils';
import ListingChooser from '@/components/ListingChooser';
import { useListingChoice } from '@/hooks/useListingChoice';
import { cityFlag as flag } from '@/utils/cityFlag';
import { daysClass } from '@/utils/daysClass';
import { useToast } from '@/context/ToastContext';
import { tripPriceDisplay, ZERO_DEC_CCY } from '@/utils/tripPriceUtils';
import { isEntreprise } from '@/utils/entreprise';
import { usePropositionDraft } from '@/hooks/usePropositionDraft';
import { isNegotiable as isTargetNegotiable } from '@/utils/propositionPricing';

const API = `${BACKEND_URL}/api`;
const STRIPE_PK = STRIPE_PUBLISHABLE_KEY;

/* ── helpers ── */
const initials = (f, l) => `${(f || '?')[0]}${(l || '?')[0]}`.toUpperCase();
const AV_COLORS = ['#1a4a7a','#0e7a68','#C8832A','#4B44C8','#1B4332','#B91C1C'];
const avColor = (id) => AV_COLORS[(String(id||'').split('').reduce((a,c)=>a+c.charCodeAt(0),0))%AV_COLORS.length];
function daysUntilFR(dateStr) {
  if (!dateStr) return null;
  const [depY, depM, depD] = dateStr.split('-').map(Number);
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' });
  const [todY, todM, todD] = fmt.format(new Date()).split('-').map(Number);
  return Math.round((Date.UTC(depY, depM - 1, depD) - Date.UTC(todY, todM - 1, todD)) / 86400000);
}
function daysFromNow(d) {
  if (!d) return '·';
  const diff = daysUntilFR(d);
  if (diff === null || diff < 0) return `J${diff ?? ''}`;
  if (diff === 0) return 'Auj.';
  return `J-${diff}`;
}
function makeRef(id, type) {
  if (!id) return '·';
  return `${type === 'trajet' ? 'C2S-TRJ' : 'C2S-COL'}-${String(id).slice(-4).toUpperCase()}`;
}
function normalize(item, type) {
  const user = item.author || item.owner || {};
  const score = user.avg_rating ? user.avg_rating.toFixed(1).replace('.', ',') : null;
  return {
    raw: item, id: item.id, type,
    from: item.departure_city, to: item.arrival_city,
    days: daysFromNow(item.departure_date),
    daysNum: daysUntilFR(item.departure_date),
    dateLimit: new Date(item.departure_date).toLocaleDateString('fr-FR'),
    price: type === 'trajet' ? tripPriceDisplay(item).amount : item.price_offered,
    unit: type === 'trajet' ? tripPriceDisplay(item).unit : ccySym(item.primary_currency || 'EUR'),
    weight: type === 'trajet' ? item.available_weight_kg : item.weight_kg,
    hot: (() => { const d = daysUntilFR(item.departure_date); return d !== null && d > 0 && d <= 5; })(),
    ref: makeRef(item.id, type),
    user: {
      id: item.user_id,
      name: `${user.first_name || 'Utilisateur'} ${(user.last_name_initial || user.last_name || '').slice(0, 1).toUpperCase()}.`.trim(),
      ini: initials(user.first_name, user.last_name_initial || user.last_name),
      color: avColor(item.user_id),
      score: score || 'Nouveau',
      trips: user.rating_count || 0,
      verified: user.is_verified || false,
      pro: isEntreprise(user),
      subscribed: !!user.pro_subscribed,
    },
  };
}

/* ─── Stripe.js ─── */
function useStripeJs() {
  const [stripe, setStripe] = useState(null);
  const [stripeError, setStripeError] = useState('');
  const [retryable, setRetryable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!STRIPE_PK) { setStripeError('REACT_APP_STRIPE_PUBLISHABLE_KEY non configurée.'); return; }
    if (!STRIPE_PK.startsWith('pk_test_') && !STRIPE_PK.startsWith('pk_live_')) {
      setStripeError(`Clé Stripe invalide ("${STRIPE_PK.slice(0, 12)}...").`); return;
    }
    let alive = true;
    setStripeError(''); setRetryable(false);
    // Chargement partagé, avec nouvelles tentatives (utils/stripeJs.js) ; « Réessayer » relance après trois échecs
    loadStripeJs()
      .then((Stripe) => { if (alive) setStripe(Stripe(STRIPE_PK)); })
      .catch((e) => { if (alive) { setStripeError(e.message); setRetryable(true); } });
    return () => { alive = false; };
  }, [attempt]);
  const retryStripe = () => setAttempt((n) => n + 1);
  return { stripe, stripeError, retryable, retryStripe };
}

/* ─── EscrowPanel (Réserver avec Séquestre) ─── */
function EscrowPanel({ a, price, tripPrice, currency, conversionMissing, onSuccess, selectedId, feeSummary, recapRows }) {
  const [message, setMessage] = useState('');
  const [step, setStep] = useState('form');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [missionId, setMissionId] = useState('');
  const cardRef = useRef(null);
  const [cardElement, setCardElement] = useState(null);
  const { stripe, stripeError, retryable: stripeRetryable, retryStripe } = useStripeJs();
  const [pendingIds, setPendingIds] = useState(new Set());
  const [reserved, setReserved] = useState(null);
  // Le prix et la devise viennent du brouillon commun (usePropositionDraft) : le panneau affiche, il ne calcule pas
  const tripCurrency = a?.raw?.primary_currency || 'EUR';
  const escrowCurrency = currency || tripCurrency;
  const sym = ccySym(escrowCurrency);
  const approx = escrowCurrency !== tripCurrency ? '≈ ' : '';
  useEffect(() => {
    if (!a?.id) return;
    axios.get(`${API}/propositions/sent?status=PENDING&limit=50`)
      .then(res => {
        const ids = (res.data?.data || []).filter(p => p.trip_id === a.id).map(p => p.shipment_id);
        setPendingIds(new Set(ids));
      }).catch(() => {});
  }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 'card' || !stripe || !cardRef.current) return;
    const elements = stripe.elements();
    const card = elements.create('card', {
      style: { base: { fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", fontSize: '15px', color: '#0F172A', '::placeholder': { color: '#94A3B8' } }, invalid: { color: '#DC2626' } },
      hidePostalCode: true,
    });
    card.mount(cardRef.current);
    setCardElement(card);
    return () => { try { card.unmount(); } catch {} setCardElement(null); };
  }, [step, stripe]);

  const hasPending = selectedId ? pendingIds.has(selectedId) : false;

  const handleReserve = async () => {
    if (!selectedId) { setError('Sélectionnez votre annonce colis.'); return; }
    if (!price) { setError(conversionMissing ? 'Conversion indisponible pour cette devise : choisissez-en une autre.' : 'Ce trajet n\'a pas de prix à réserver.'); return; }
    if (stripeError) {
      if (stripeRetryable) retryStripe();
      setError(stripeRetryable ? `${stripeError} Nouvel essai en cours, réessayez dans un instant.` : stripeError);
      return;
    }
    setError(''); setStep('loading');
    try {
      const { data } = await axios.post(`${API}/payments/v1/reserve`, {
        shipment_id: selectedId, trip_id: a.id,
        proposed_price: tripPrice,
        currency: escrowCurrency,
        message: message.trim() || undefined,
      });
      if (data.simulated) { setError("Paiement temporairement indisponible — veuillez réessayer ou contacter le support."); setStep('form'); return; }
      setReserved({ amount: data.amount, currency: data.currency });
      setClientSecret(data.client_secret); setMissionId(data.mission_id); setStep('card');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Erreur lors de la réservation.';
      if (status === 409 && detail.includes('réservation')) {
        setError('Vous avez déjà une réservation en cours pour ce colis.');
        setPendingIds(prev => new Set([...prev, selectedId]));
      } else if (status === 409 && detail.includes('mission')) {
        setError('Un accord est déjà en cours pour cette annonce — aucune nouvelle réservation possible.');
      } else { setError(detail); }
      setStep('form');
    }
  };

  const handleConfirmCard = async () => {
    if (!stripe || !cardElement || !clientSecret || confirming) return;
    setConfirming(true); setError('');
    try {
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement } });
      if (result.error) setError(result.error.message || 'Paiement refusé.');
      else { setStep('done'); onSuccess?.({ mission_id: missionId }); }
    } catch { setError('Erreur inattendue lors de la confirmation.'); }
    finally { setConfirming(false); }
  };

  if (step === 'done') return (
    <div className="dp-success-note" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 6 }}>✅</div>
      <strong>Paiement réservé avec succès</strong>
      <p style={{ fontSize: 12, marginTop: 6, color: '#166534' }}>
        Votre carte n'a pas encore été débitée.<br/>
        Le paiement sera confirmé une fois le transporteur accepté.
      </p>
    </div>
  );

  // Encart « Réserver avec paiement sécurisé » : après le montant et les frais (ordre commun des panneaux)
  const escrowHeader = (
      <div className="dp-escrow-header">
        <div className="dp-escrow-side">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
        <div className="dp-escrow-header-content">
          <div className="dp-escrow-title">Réserver avec paiement sécurisé</div>
          <div className="dp-escrow-sub">Votre carte ne sera débitée qu'après accord du transporteur</div>
          <div className="dp-escrow-chips">
            <span className="dp-escrow-chip dp-escrow-chip--green">✓ Stripe</span>
            <span className="dp-escrow-chip">🔒 Paiement sécurisé</span>
            <span className="dp-escrow-chip">↩ Remboursable</span>
          </div>
        </div>
      </div>
  );

  return (
    <div className="dp-escrow-inner" data-testid="dp-escrow-panel">
      {error && <div className="dp-error-note">{error}</div>}
      {step === 'card' && escrowHeader}
      {(step === 'form' || step === 'loading') && (
        <>
          {/* Même calcul que « Proposer », sans total : le total à réserver est dans les frais Colis2Ship, juste après */}
          <div className="dp-calc-box" data-testid="dp-escrow-recap" style={{ marginTop: 10 }}>{recapRows}</div>
          {!price && (
            <div className="dp-hint">{conversionMissing ? 'Conversion en cours…' : 'Sélectionnez un colis'}</div>
          )}
          {price && approx && (
            <div className="dp-hint" data-testid="dp-escrow-converted">
              Prix du trajet converti au taux du jour ; le montant exact est confirmé à la réservation.
            </div>
          )}
          {feeSummary}
          {escrowHeader}
          <div>
            <label className="dp-label">Message (optionnel)</label>
            <input className="dp-input" type="text" maxLength={300} value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Bonjour, je souhaite réserver…"
              disabled={step === 'loading'} />
          </div>
          {hasPending && (
            <div className="dp-warning-note">
              ⏳ Réservation en attente pour ce trajet. <a href="/app/propositions" style={{ color: '#d97706', fontWeight: 600 }}>Gérer mes propositions →</a>
            </div>
          )}
          <button className="dp-cta dp-cta--gold" data-testid="dp-escrow-amount" onClick={handleReserve}
            disabled={step === 'loading' || !selectedId || !price || hasPending}>
            {step === 'loading' ? '⏳ Création de la réservation…' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>Réserver {price ? `${approx}${fmtAmt(price)} ${sym}` : ''}</>}
          </button>
          <div className="dp-trust-badges">
            <span>🔒 Stripe</span>
            <span>⏱ Débité après accord</span>
            <span>↩ Remboursable</span>
          </div>
        </>
      )}
      {step === 'card' && (
        <>
          <div className="dp-info-note">ℹ️ Votre carte <strong>ne sera pas débitée maintenant</strong>, uniquement à la livraison confirmée.</div>
          {reserved && (
            <div className="dp-hint" data-testid="dp-escrow-reserved">
              Montant réservé : <strong>{fmtAmt(reserved.amount)} {ccySym(reserved.currency)}</strong>
            </div>
          )}
          {stripeError && (
            <div className="dp-error-note" data-testid="dp-stripe-error">
              {stripeError}{' '}
              {stripeRetryable && <button type="button" className="dp-link-btn" onClick={retryStripe}>Réessayer</button>}
            </div>
          )}
          <div>
            <label className="dp-label">Carte bancaire</label>
            <div ref={cardRef} style={{ border: '1.5px solid rgba(15,23,42,.15)', borderRadius: 8, padding: '12px 14px', background: '#fff', minHeight: 44 }} />
            <div className="dp-hint" style={{ marginTop: 4 }}>🔒 Chiffré par Stripe · Jamais transmis à Colis2Ship</div>
          </div>
          {confirming
            ? <div style={{ textAlign: 'center', padding: '16px 0', color: '#374151' }}>⏳ Confirmation en cours…</div>
            : <button className="dp-cta dp-cta--gold" onClick={handleConfirmCard} disabled={!stripe || !cardElement}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                Confirmer la réservation
              </button>
          }
          {error && <div className="dp-error-note">{error}</div>}
        </>
      )}
    </div>
  );
}


/* ─── TabDemo : aperçu statique affiché sous chaque onglet pour les visiteurs non connectés ─── */
function TabDemo({ activeTab, isTrajet, isNoPrice, annoncePrice, annonceUnit, annonceCcy, annonceFrom, annonceTo, pricingMode, annoncePricePerKg, annoncePriceFixed }) {
  // isTrajet === true → annonce TRAJET → le visiteur est expéditeur → il sélectionne son COLIS
  // isTrajet === false → annonce COLIS → le visiteur est transporteur → il sélectionne son TRAJET
  const isSender = isTrajet;
  const roleLabel = isSender ? '📦 En tant qu\'expéditeur' : '✈️ En tant que transporteur';
  const pickLabel = isSender ? 'Sélection · Mon colis à envoyer' : 'Sélection · Mon trajet';
  const roleCls   = isSender ? 'dp-demo-pill--sender' : 'dp-demo-pill--carrier';
  const primaryCcy = annonceCcy || 'EUR';
  const mode = pricingMode || 'kg';
  const demoCcySym = ccySymCurrency(primaryCcy);

  // Aucune donnée inventée : le visiteur voit la place de son colis ou de son trajet, qu'il choisira une fois connecté
  const listingBlock = (
    <>
      <label className="dp-label">{pickLabel}</label>
      <div className="dp-listing-cards">
        <div className="dp-listing-card">
          <div className="dp-listing-card-body">
            <div className="dp-listing-card-title">{isSender ? 'Votre colis' : 'Votre trajet'}</div>
            <div className="dp-listing-card-sub">
              {isSender ? 'Connectez-vous pour choisir le colis à envoyer.' : 'Connectez-vous pour choisir le trajet à proposer.'}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const ccyBlock = (
    <>
      <span className="dp-section-label">Devise de la proposition</span>
      <div className="cs-seg-info cs-seg-info--star">
        <strong>{primaryCcy}</strong> : devise de l'annonce. Une fois connecté, vous choisissez parmi les devises qu'elle accepte.
      </div>
    </>
  );

  const calcBlock = isSender ? (
    mode === 'libre' ? null : mode === 'fixe' ? (
      <div className="dp-calc-box">
        <div className="dp-recap-row"><span>Prix fixe · forfait</span><span>{annoncePrice} {annonceUnit}</span></div>
      </div>
    ) : (
      <div className="dp-calc-box">
        <div className="dp-recap-row"><span>Poids du colis</span><span>celui de votre colis</span></div>
        <div className="dp-recap-row"><span>Tarif du transporteur</span><span>{annoncePrice} {annonceUnit}</span></div>
      </div>
    )
  ) : isNoPrice ? (
    <div className="dp-info-note">💬 L'expéditeur n'a pas fixé de budget. Utilisez <strong>Négocier</strong> pour proposer votre tarif.</div>
  ) : (
    <div className="dp-calc-box">
      <div className="dp-recap-row"><span>Budget proposé</span><span>{annoncePrice} {annonceUnit}</span></div>
    </div>
  );

  if (activeTab === 'direct') {
    return (
      <div className="dp-demo">
        <div className="dp-demo-eyebrow">Aperçu · Propose direct</div>
        <div className="dp-demo-body">
          <span className={`dp-demo-pill ${roleCls}`}>{roleLabel}</span>
          {calcBlock}
          {listingBlock}
          {ccyBlock}
          <button className="dp-cta dp-cta--gold" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Envoyer ma proposition
          </button>
        </div>
      </div>
    );
  }

  if (activeTab === 'negocier') {
    return (
      <div className="dp-demo">
        <div className="dp-demo-eyebrow">Aperçu · Négocier</div>
        <div className="dp-demo-body">
          <span className={`dp-demo-pill ${roleCls}`}>{roleLabel}</span>
          {listingBlock}
          {ccyBlock}
          <div className="dp-label">Votre prix proposé ({demoCcySym})</div>
          <div className="dp-price-input">
            <span>{demoCcySym}</span>
            <span className="dp-demo-price-val">—</span>
          </div>
          <div className="dp-label">Message</div>
          <div className="dp-demo-textarea">{isSender ? 'Votre message au transporteur…' : 'Votre message à l’expéditeur…'}</div>
          <button className="dp-cta dp-cta--navy" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>
            Envoyer ma négociation
          </button>
        </div>
      </div>
    );
  }

  if (activeTab === 'contacter') {
    return (
      <div className="dp-demo">
        <div className="dp-demo-eyebrow">Aperçu · Contacter</div>
        <div className="dp-demo-body">
          <div className="dp-info-note">💬 Posez une question avant de vous engager. Aucune offre n'est envoyée à cette étape.</div>
          <div className="dp-demo-textarea">Votre message…</div>
          <button className="dp-cta dp-cta--teal" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Envoyer le message
          </button>
        </div>
      </div>
    );
  }

  if (activeTab === 'signaler') {
    return (
      <div className="dp-demo">
        <div className="dp-demo-eyebrow">Aperçu · Signaler</div>
        <div className="dp-demo-body">
          <div className="dp-warning-note" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px' }}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
            <span>Signalement examiné par la modération sous 24h.</span>
          </div>
          <div className="dp-label">Motif</div>
          <div className="dp-demo-radio-list">
            {REPORT_REASONS.map((opt, i) => (
              <div key={opt.value} className={`dp-demo-radio-item${i === 0 ? ' active' : ''}`}>
                <div className={`dp-demo-radio dp-demo-radio--signaler${i === 0 ? ' on' : ''}`} />
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
          <div className="dp-demo-warn">🔒 Signalement confidentiel. L'auteur ne sera pas notifié.</div>
          <button className="dp-cta dp-cta--red" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
            Envoyer le signalement
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/* ─── TabDemoEscrow : aperçu statique du panneau séquestre pour visiteurs non connectés ─── */
function TabDemoEscrow({ annonceUnit, annoncePricePerKg, annoncePriceFixed, pricingMode }) {
  const mode = pricingMode || 'kg';
  const tripSym = (annonceUnit || '€').split('/')[0];
  const rawFixed = Number(annoncePriceFixed);
  // Forfait : le montant est connu ; au kilo, il dépend du colis que le visiteur choisira une fois connecté
  const fixedPrice = mode === 'fixe' && !isNaN(rawFixed) && rawFixed > 0 ? rawFixed : null;

  return (
    <div className="dp-escrow-inner" data-testid="dp-escrow-demo">
      <div>
        <label className="dp-label">Sélection · Mon colis à envoyer</label>
        <div className="dp-listing-cards">
          <div className="dp-listing-card">
            <div className="dp-listing-card-body">
              <div className="dp-listing-card-title">Votre colis</div>
              <div className="dp-listing-card-sub">Connectez-vous pour choisir le colis à envoyer.</div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <label className="dp-label">Montant à sécuriser ({tripSym})</label>
        <div className="dp-calc-box" style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
          {fixedPrice != null ? `${fmtAmt(fixedPrice)} ${tripSym}` : `${fmtAmt(Number(annoncePricePerKg))} ${tripSym} / kg × le poids de votre colis`}
        </div>
      </div>
      {/* Même ordre que le panneau réel : montant, puis l'encart et le reste */}
      <div className="dp-escrow-header">
        <div className="dp-escrow-side">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
        <div className="dp-escrow-header-content">
          <div className="dp-escrow-title">Réserver avec paiement sécurisé</div>
          <div className="dp-escrow-sub">Votre carte ne sera débitée qu'après accord du transporteur</div>
          <div className="dp-escrow-chips">
            <span className="dp-escrow-chip dp-escrow-chip--green">✓ Stripe</span>
            <span className="dp-escrow-chip">🔒 Paiement sécurisé</span>
            <span className="dp-escrow-chip">↩ Remboursable</span>
          </div>
        </div>
      </div>
      <div>
        <label className="dp-label">Message (optionnel)</label>
        <input className="dp-input" type="text" placeholder="Bonjour, je souhaite réserver…" readOnly />
      </div>
      <button className="dp-cta dp-cta--gold" disabled>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        {fixedPrice != null ? `Réserver ${fmtAmt(fixedPrice)} ${tripSym}` : 'Réserver'}
      </button>
    </div>
  );
}

const REPORT_REASONS = [
  { value: 'misleading',    label: 'Contenu trompeur ou incorrect' },
  { value: 'suspicious',    label: 'Profil suspect' },
  { value: 'scam',          label: "Tentative d'arnaque" },
  { value: 'inappropriate', label: 'Comportement inapproprié' },
  { value: 'other',         label: 'Autre' },
];

/* ═══════════════════════════════════════
   PROPOSE PAGE
════════════════════════════════════════ */
export default function ProposePage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();

  /* ── fetch annonce ── */
  const [annonce, setAnnonce] = useState(() => {
    // Fast path: annonce pre-loaded from AnnoncesPage via router state
    return location.state?.annonce || null;
  });
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(!location.state?.annonce);

  useEffect(() => {
    if (!type || !id) return undefined;
    let alive = true;
    // Venue de la liste : affichée tout de suite, puis complétée (la liste n'a pas les photos d'un colis, T27)
    const fromList = !!location.state?.annonce;
    if (!fromList) setLoading(true);
    setFetchError('');
    const kind = type === 'trajet' ? 'trips' : 'shipments';
    // Route publique : un lien partagé s'ouvre sans compte (T26). Une annonce qui n'est plus ouverte n'y est pas ;
    // un membre connecté la relit par la route des membres (son annonce en attente de validation, par exemple)
    axios.get(`${API}/public/${kind}/${id}`)
      .catch((e) => (e.response?.status === 404 && user ? axios.get(`${API}/${kind}/${id}`) : Promise.reject(e)))
      .then((res) => { if (alive) setAnnonce(normalize(res.data?.data || res.data, type)); })
      .catch((e) => {
        if (!alive) return;
        const status = e.response?.status;
        if (status === 404) { setAnnonce(null); setFetchError("Cette annonce n'est plus disponible."); }
        else if (!fromList) setFetchError("Impossible de charger l'annonce.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [type, id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── sidebar tab ── */
  const [activeTab, setActiveTab] = useState('direct');
  // « Écrire à X », dans la colonne de gauche : ouvre le panneau dans la carte d'action et l'amène à l'écran
  const openTab = (tab) => {
    setActiveTab(tab);
    document.getElementById('ap-action-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ── Onboarding modal (guest only, 10s delay) ── */
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (user) return;
    const t = setTimeout(() => setShowOnboarding(true), 10000);
    return () => clearTimeout(t);
  }, [user]);

  /* ── my listings (for propose/negotiate/escrow) ── */
  const [myListings, setMyListings] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const meSide = type === 'trajet' ? 'sender' : 'carrier';
  const needsRole = meSide === 'sender' ? 'ROLE_SENDER' : 'ROLE_CARRIER';
  const hasRole = !!user && (user.roles || []).includes(needsRole);
  const isOwner = !!user && annonce?.user?.id === user.id;
  const canPropose = !!user && hasRole && !isOwner;
  const kycOk = canDo(user, activeTab === 'contacter' ? 'contact' : activeTab === 'sequestre' ? 'reserve' : 'propose');
  const canPay = meSide === 'sender';

  useEffect(() => {
    if (!annonce || !canPropose) return;
    setLoadingMine(true);
    axios.get(`${API}/${meSide === 'sender' ? 'shipments/mine' : 'trips/mine'}`)
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        const filtered = items.filter(i => meSide === 'sender'
          ? i.status === 'PUBLISHED'
          : ['ACTIVE', 'PUBLISHED'].includes(i.status));
        setMyListings(filtered);
      })
      .catch(() => {})
      .finally(() => setLoadingMine(false));
  }, [annonce, canPropose, meSide]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── form state ── */
  const [message, setMessage] = useState('');
  const [contactMsg, setContactMsg] = useState('');
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [escrowSuccess, setEscrowSuccess] = useState(false);
  const [pendingProposedIds, setPendingProposedIds] = useState(new Set());
  /* ── top listing selector — sidebar top (sender & carrier) ── */
  const topTripDays = useMemo(() => {
    const diff = daysUntilFR(annonce?.raw?.departure_date);
    return diff !== null && diff > 0 ? `J+${diff}` : null;
  }, [annonce]);

  useEffect(() => {
    if (!annonce?.id) return;
    Promise.all([
      axios.get(`${API}/propositions/sent?status=PENDING&limit=50`),
      axios.get(`${API}/propositions/sent?status=ACCEPTED&limit=50`),
    ]).then(([pRes, aRes]) => {
      const data = [...(pRes.data?.data || []), ...(aRes.data?.data || [])];
      const ids = meSide === 'sender'
        ? data.filter(p => p.trip_id === annonce.id).map(p => p.shipment_id)
        : data.filter(p => p.shipment_id === annonce.id).map(p => p.trip_id);
      setPendingProposedIds(new Set(ids));
    }).catch(() => {});
  }, [annonce?.id, meSide]); // eslint-disable-line react-hooks/exhaustive-deps


  // Mes annonces face à l'annonce visée : présélection de la plus pertinente, choix partagé avec la fenêtre
  // « Faire une proposition » (hooks/useListingChoice.js, components/ListingChooser.jsx)
  const { ranked, selectedId, selected: topSelected, choose: chooseListing } = useListingChoice({
    meSide, target: annonce?.raw, listings: myListings, proposedIds: pendingProposedIds,
  });
  const alreadyProposed = selectedId ? pendingProposedIds.has(selectedId) : false;

  // Avis rédigés reçus par l'auteur de l'annonce : route publique (un visiteur les voit aussi), auteur en vue publique
  const [avis, setAvis] = useState({ data: [], total: 0 });
  const authorId = annonce?.user?.id;
  useEffect(() => {
    if (!authorId) return undefined;
    let alive = true;
    axios.get(`${API}/public/users/${authorId}/ratings`, { params: { limit: 10 } })
      .then((res) => { if (alive) setAvis({ data: res.data?.data || [], total: res.data?.total || 0 }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [authorId]);
  const avisRediges = avis.data.filter((r) => (r.comment || '').trim()).slice(0, 2);

  // Choix par défaut : « Réserver maintenant » côté expéditeur quand le trajet a un prix (maquette C), « Proposer » côté
  // transporteur ; sans prix (trajet au tarif libre, colis sans budget), « Négocier », seul choix possible (2026-10-28)
  useEffect(() => {
    if (!annonce) return;
    const noPrice = type === 'trajet' ? annonce.raw?.pricing_mode === 'libre' : !annonce.raw?.price_offered;
    setActiveTab(noPrice ? 'negocier' : meSide === 'sender' ? 'sequestre' : 'direct');
  }, [annonce?.id, meSide]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── parsers v1 fallback ── */

  const descriptionText = useMemo(() => {
    if (!annonce) return '';
    return (annonce.raw?.description || '').trim();
  }, [annonce]);

  const pkgCategory = useMemo(() => {
    if (type !== 'colis' || !annonce) return '';
    if (annonce.raw?.categories?.length > 0) {
      const raw = annonce.raw.categories;
      return (raw.length > 1 ? raw.filter(c => c !== 'Autre') : [...raw]).join(', ');
    }
    const m = (annonce.raw?.description || '').match(/Cat[éé]gorie\s*:?\s*([^·\n]+)/i);
    return m ? m[1].trim() : '';
  }, [annonce, type]);

  const shipConstraints = useMemo(() => {
    if (type !== 'colis' || !annonce) return [];
    return Array.isArray(annonce.raw?.constraints) ? annonce.raw.constraints : [];
  }, [annonce, type]);
  const shipConstraintCustom = useMemo(() => type === 'colis' ? (annonce?.raw?.constraint_custom || '') : '', [annonce, type]);
  const shipCarrierPrefs = useMemo(() => type === 'colis' && Array.isArray(annonce?.raw?.carrier_preferences) ? annonce.raw.carrier_preferences : [], [annonce, type]);
  const shipMsg = useMemo(() => type === 'colis' ? (annonce?.raw?.message_to_carriers || '') : '', [annonce, type]);
  const isUrgentColis = useMemo(() =>
    type === 'colis' && Array.isArray(annonce?.raw?.constraints) && annonce.raw.constraints.includes('urgent'),
  [annonce, type]);

  /* ── currency logic ── */
  const listingCurrencies = useMemo(() => normalizeCurrencies(annonce?.raw), [annonce]);
  const mine = useMemo(() => myListings.find(l => l.id === selectedId), [myListings, selectedId]);
  const myListingCurrencies = useMemo(() => normalizeCurrencies(mine), [mine]);
  const commonCurrencies = useMemo(() => getCommonCurrencies(listingCurrencies, myListingCurrencies), [listingCurrencies, myListingCurrencies]);
  const currencyCompatible = commonCurrencies.length > 0;

  // Prix, devise, garde-fous et requête : brouillon commun aux quatre écrans de proposition
  const target = annonce?.raw;
  const draft = usePropositionDraft({ meSide, target, mine, escrow: meSide === 'sender' });
  const { price, setPrice, currency, setCurrency, conversionMissing, targetPrice } = draft;
  const negDiff = draft.diff;

  /* ── actions ── */
  const submitProposal = async (mode) => {
    if (!selectedId) { toast.error(meSide === 'sender' ? "Choisissez l'annonce colis." : 'Choisissez le trajet.'); return; }
    const isNegotiation = mode === 'negotiation';
    if (!draft.canSubmit(mode)) return;
    setSubmitting(true);
    try {
      const payload = draft.payload(mode);
      if (message.trim()) payload.message = message.trim();
      const r = await axios.post(`${API}/propositions`, payload);

      if (isNegotiation && annonce?.raw?.user_id) {
        try {
          const dm = await axios.post(`${API}/conversations/direct`, { user_id: annonce.raw.user_id });
          const route = `${annonce.raw.departure_city} → ${annonce.raw.arrival_city}`;
          const offerMsg = [
            `💬 Contre-offre : ${price} ${ccySym(currency || 'EUR')} pour ${route}`,
            message.trim() ? `Message : ${message.trim()}` : null,
            `→ Réf. proposition #${r.data?.id?.slice(-6) || '...'}`,
          ].filter(Boolean).join('\n');
          await axios.post(`${API}/conversations/${dm.data.id}/messages`, { content: offerMsg });
        } catch { /* non bloquant */ }
      }

      toast.success(isNegotiation ? 'Contre-offre envoyée ! Vous serez notifié dès réponse.' : 'Proposition envoyée ! Vous serez notifié dès réponse.');
      setTimeout(() => navigate('/app/propositions?tab=sent'), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi."); }
    finally { setSubmitting(false); }
  };

  const submitContact = async () => {
    if (!contactMsg.trim()) return;
    setSubmitting(true);
    try {
      if (!annonce?.raw?.user_id) throw new Error('user_id manquant');
      const dm = await axios.post(`${API}/conversations/direct`, { user_id: annonce.raw.user_id });
      await axios.post(`${API}/conversations/${dm.data.id}/messages`, { content: contactMsg.trim() });
      toast.success('Message envoyé !');
      setContactMsg('');
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du message."); }
    finally { setSubmitting(false); }
  };

  const submitReport = async () => {
    if (!reportDetails.trim()) { toast.error('Veuillez décrire la situation.'); return; }
    setSubmitting(true);
    try {
      const content = `🚩 SIGNALEMENT — ${annonce?.ref || id}\nMotif : ${reportReason || 'Non précisé'}\nDétails : ${reportDetails.trim()}`;
      await axios.post(`${API}/reports`, { target_type: type === 'trajet' ? 'trip' : 'shipment', target_id: id, reason: reportReason, details: reportDetails.trim() })
        .catch(() => axios.post(`${API}/conversations/direct`, { user_id: annonce?.raw?.user_id }).then(dm =>
          axios.post(`${API}/conversations/${dm.data.id}/messages`, { content })
        ));
      toast.success("Signalement transmis à l'équipe Colis2Ship.");
      setReportDetails('');
    } catch { toast.success('Signalement reçu. Notre équipe examinera sous 24h.'); setReportDetails(''); }
    finally { setSubmitting(false); }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.info('Lien copié !');
  };

  const handleWAShare = () => {
    const msg = encodeURIComponent(`Regarde ce trajet ${a?.from} → ${a?.to} sur Colis2Ship 🚀 ${window.location.href}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const annonceBaseCcy = annonce?.raw?.primary_currency || 'EUR';

  /* ── loading / error states ── */
  if (loading) return (
    <div className="dp-root">
      <div className="dp-fullpage-state"><div className="dp-spin" /><span>Chargement de l'annonce…</span></div>
    </div>
  );
  if (fetchError || !annonce) return (
    <div className="dp-root">
      <div className="dp-fullpage-state">
        <span style={{ fontSize: 32 }}>⚠️</span>
        <span>{fetchError || 'Annonce introuvable.'}</span>
        <button className="dp-cta dp-cta--ghost" style={{ maxWidth: 200, marginTop: 8 }} onClick={() => navigate('/annonces')}>← Retour aux annonces</button>
      </div>
    </div>
  );

  const VALID_TARGET_STATUSES = type === 'trajet' ? ['PUBLISHED', 'ACTIVE'] : ['PUBLISHED'];
  if (!isOwner && !VALID_TARGET_STATUSES.includes(annonce.raw?.status)) return (
    <div className="dp-root">
      <div className="dp-fullpage-state">
        <span style={{ fontSize: 32 }}>🔒</span>
        <span>Cette annonce n'est plus disponible.</span>
        <button className="dp-cta dp-cta--ghost" style={{ maxWidth: 200, marginTop: 8 }} onClick={() => navigate('/annonces')}>← Retour aux annonces</button>
      </div>
    </div>
  );

  const a = annonce;
  const aDaysNum = a.daysNum ?? (!a.raw?.departure_date ? null : Math.ceil((new Date(a.raw.departure_date) - Date.now()) / 86400000));
  const isTrajet = type === 'trajet';
  const tripCats = isTrajet
    ? (a.raw?.accepted_categories || [])
    : [];
  const tripConds = isTrajet
    ? (Array.isArray(a.raw?.conditions) ? a.raw.conditions : [])
    : [];
  const isFirm = isTrajet && a.raw?.counter_offers === false;
  const isLibre = isTrajet && a.raw?.pricing_mode === 'libre';
  const isNoPrice = !isTrajet && !a.raw?.price_offered;
  const isNegotiable = isTargetNegotiable(meSide, a.raw);
  const author = a.raw?.author || a.raw?.owner || {};
  const modeLabel = (a.raw?.transport_modes || []).map((t) => sansEmoji(TRANSPORT_LABELS[t] || t)).join(' · ');
  const authorMessage = (isTrajet ? a.raw?.message_to_senders : shipMsg) || '';
  // Lignes du calcul du prix (poids × tarif, forfait, budget) : communes à « Proposer » et à « Réserver », sans total —
  // le total suit, dans les frais Colis2Ship (div.sfs) de l'expéditeur — décisions du 2026-10-29
  const priceRecapRows = (
    <>
      {isTrajet && !isLibre && (a.raw?.pricing_mode === 'kg' || !a.raw?.pricing_mode) && myListings.find(s => s.id === selectedId)?.weight_kg && (
        <>
          <div className="dp-recap-row">
            <span>Poids du colis</span>
            <span>{kg(myListings.find(s => s.id === selectedId).weight_kg)}</span>
          </div>
          <div className="dp-recap-row">
            <span>Tarif du transporteur</span>
            <span>{Number(a.raw?.price_per_kg).toLocaleString('fr-FR', ZERO_DEC_CCY.has(annonceBaseCcy) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {ccySymCurrency(annonceBaseCcy)}/kg</span>
          </div>
        </>
      )}
      {isTrajet && !isLibre && a.raw?.pricing_mode === 'fixe' && (
        <div className="dp-recap-row">
          <span>Prix fixe forfait</span>
          <span>{a.raw?.price_fixed} {ccySymCurrency(annonceBaseCcy)}</span>
        </div>
      )}
      {!isTrajet && (
        <div className="dp-recap-row">
          <span>Budget proposé</span>
          <span>{isNoPrice ? 'Budget libre' : `${a.price} ${a.unit}`}</span>
        </div>
      )}
    </>
  );

  return (
    <div className="dp-root" data-testid="propose-page">
      {/* ══ EN-TÊTE (direction C) : l'auteur, la route, l'essentiel en pastilles ══ */}
      <header className="ap-hero-wrap">
        <div className="ap-top">
          <button className="ap-back" onClick={() => navigate('/annonces', { state: { restoreFilters: location.state?.fromFilters } })}>
            <ArrowLeft size={16} aria-hidden="true" /> Toutes les annonces
          </button>
          <span className="ap-ref">
            {a.ref}
            <span className={`dp-days-badge dp-days-badge--${daysClass(aDaysNum)}`}>{a.days}</span>
          </span>
        </div>
        <section className="ap-hero" aria-label={isTrajet ? 'Le transporteur et son trajet' : "L'expéditeur et son colis"}>
          <MemberAvatar firstName={author.first_name || a.user.name} lastName={author.last_name || ''} size={104} />
          <div className="ap-hero-body">
            <p className="ap-hero-lead">
              {isTrajet
                ? <>{a.user.name} part le <strong>{a.dateLimit}</strong>{modeLabel && <> · {modeLabel}</>}</>
                : <>{a.user.name} cherche un transporteur avant le <strong>{a.dateLimit}</strong></>}
            </p>
            <h1 className="ap-hero-title dp-title">
              {flag(a.from)} {a.from} <span className="ap-hero-arrow" aria-hidden="true">→</span> {flag(a.to)} {a.to}
            </h1>
            <div className="ap-chips">
              <span className="ap-chip ap-chip--type">
                {isTrajet ? <Plane size={14} aria-hidden="true" /> : <Package size={14} aria-hidden="true" />}
                {isTrajet ? 'Trajet' : 'Colis'}
              </span>
              {!isTrajet && a.raw?.title && <span className="ap-chip">{a.raw.title}</span>}
              {a.weight != null && <span className="ap-chip">{isTrajet ? `${kg(a.weight)} de libre` : kg(a.weight)}</span>}
              {isTrajet && a.raw?.max_weight_per_parcel != null && <span className="ap-chip">{kg(a.raw.max_weight_per_parcel)} max. par colis</span>}
              {isTrajet && a.raw?.max_parcels != null && <span className="ap-chip">{a.raw.max_parcels} colis au plus</span>}
              {isTrajet && a.raw?.arrival_date && <span className="ap-chip">Arrivée prévue le {new Date(a.raw.arrival_date).toLocaleDateString('fr-FR')}</span>}
              {!isTrajet && a.raw?.dimensions && <span className="ap-chip">{a.raw.dimensions}</span>}
              {(a.hot || isUrgentColis) && <span className="ap-chip ap-chip--urgent"><Zap size={14} aria-hidden="true" />Urgent</span>}
              {a.user.pro && <span className="ap-chip ap-chip--neutre">Entreprise</span>}
              {a.user.subscribed && <span className="ap-chip ap-chip--pro" title="Transporteur abonné PRO">PRO</span>}
            </div>
          </div>
        </section>
      </header>

      {lightboxSrc && (
        <div className="dp-lightbox" onClick={() => setLightboxSrc(null)}>
          <img className="dp-lightbox-img" src={lightboxSrc} alt="Photo agrandie" />
          <button className="dp-lightbox-close" onClick={() => setLightboxSrc(null)} aria-label="Fermer">✕</button>
        </div>
      )}

      <div className="dp-container">

        <div className="dp-layout">

          {/* ══════ COLONNE PRINCIPALE ══════ */}
          <main className="dp-main ap-main">

            {/* Qui est l'auteur ? */}
            <section className="ap-section" aria-labelledby="ap-qui">
              <h2 id="ap-qui" className="ap-h2">Qui est {a.user.name} ?</h2>
              <VerificationBadges user={author} />
              <p className="ap-muted">
                ★ {a.user.score}{a.user.trips > 0 && ` · ${a.user.trips} avis`}
                {author.created_at && ` · membre depuis le ${new Date(author.created_at).toLocaleDateString('fr-FR')}`}
              </p>
              {authorMessage && (
                <blockquote className="ap-quote">« {authorMessage} »</blockquote>
              )}
              <div className="ap-actions-row">
                {!isOwner && (
                  <button type="button" data-testid="dp-tab-contacter"
                    className={`ap-btn-outline${activeTab === 'contacter' ? ' active' : ''}`}
                    onClick={() => openTab('contacter')}>
                    <Mail size={16} aria-hidden="true" /> Écrire à {a.user.name}
                  </button>
                )}
                <button type="button" className="ap-btn-link" onClick={() => navigate('/app/profile/' + a.user.id)}>Voir le profil</button>
              </div>
            </section>

            {/* L'annonce en détail */}
            <section className="ap-card" aria-labelledby="ap-detail">
              <h2 id="ap-detail" className="ap-h3">{isTrajet ? 'Le trajet' : 'Le colis'}</h2>
              <dl className="ap-pairs">
                {isTrajet ? (
                  <>
                    <div><dt>Départ</dt><dd>{a.dateLimit}</dd></div>
                    {a.raw?.flex_departure && a.raw.flex_departure !== 'fixe' && (
                      <div><dt>Flexibilité</dt><dd>{FLEX_LABELS[a.raw.flex_departure] || a.raw.flex_departure}</dd></div>
                    )}
                    {a.raw?.arrival_date && <div><dt>Arrivée estimée</dt><dd>{new Date(a.raw.arrival_date).toLocaleDateString('fr-FR')}</dd></div>}
                    {modeLabel && <div><dt>Transport</dt><dd>{modeLabel}</dd></div>}
                    <div><dt>Capacité</dt><dd>{a.weight != null ? kg(a.weight) : 'Non précisé'}</dd></div>
                    <div><dt>Tarif</dt><dd>{a.price ? `${a.price} ${a.unit}` : 'Non précisé'}</dd></div>
                  </>
                ) : (
                  <>
                    {pkgCategory && <div><dt>Contenu</dt><dd>{pkgCategory}</dd></div>}
                    <div><dt>Poids</dt><dd>{a.weight ? kg(a.weight) : 'Non précisé'}</dd></div>
                    {a.raw?.dimensions && <div><dt>Dimensions</dt><dd>{a.raw.dimensions}</dd></div>}
                    <div><dt>Livrer avant le</dt><dd>{a.dateLimit}</dd></div>
                    <div><dt>Budget</dt><dd>{isNoPrice ? 'Libre' : `${a.price} ${a.unit}`}</dd></div>
                  </>
                )}
                {listingCurrencies.length > 0 && (
                  <div><dt>Devises</dt><dd>{listingCurrencies.map((c) => (a.raw?.primary_currency === c ? `★ ${c}` : c)).join(' · ')}</dd></div>
                )}
              </dl>
              {descriptionText && <p className="ap-desc dp-desc-text">{descriptionText}</p>}
            </section>

            <div className="ap-grid">
              {isTrajet ? (
                <>
                  {tripCats.length > 0 && (
                    <section className="ap-card" aria-labelledby="ap-emporte">
                      <h2 id="ap-emporte" className="ap-h3">Il emporte</h2>
                      <div className="ap-chips">
                        {tripCats.map((c) => <span key={c} className="ap-chip ap-chip--soft">{CATEGORY_LABELS[c] || c}</span>)}
                      </div>
                    </section>
                  )}
                  {(tripConds.length > 0 || isFirm) && (
                    <section className="ap-card" aria-labelledby="ap-demande">
                      <h2 id="ap-demande" className="ap-h3">Il demande</h2>
                      <ul className="ap-checks">
                        {tripConds.map((c) => <li key={c}><Check size={14} aria-hidden="true" />{sansEmoji(CONDITION_LABELS[c] || c)}</li>)}
                        {isFirm && <li><Lock size={14} aria-hidden="true" />Son prix, sans négociation</li>}
                      </ul>
                    </section>
                  )}
                </>
              ) : (
                <>
                  <section className="ap-card" aria-labelledby="ap-contraintes">
                    <h2 id="ap-contraintes" className="ap-h3">Contraintes de transport</h2>
                    {(shipConstraints.length > 0 || shipConstraintCustom)
                      ? <ul className="ap-checks">
                          {shipConstraints.filter((cid) => CONSTRAINT_META[cid]).map((cid) => <li key={cid}><Info size={14} aria-hidden="true" />{CONSTRAINT_META[cid].label}</li>)}
                          {shipConstraintCustom && <li><Info size={14} aria-hidden="true" />{shipConstraintCustom}</li>}
                        </ul>
                      : <p className="ap-muted">Non précisé</p>}
                  </section>
                  <section className="ap-card" aria-labelledby="ap-prefs">
                    <h2 id="ap-prefs" className="ap-h3">Ce que {author.first_name || "l'expéditeur"} demande</h2>
                    {shipCarrierPrefs.filter((pid) => CARRIER_PREF_META[pid]).length > 0
                      ? <ul className="ap-checks">
                          {shipCarrierPrefs.filter((pid) => CARRIER_PREF_META[pid]).map((pid) => <li key={pid}><Check size={14} aria-hidden="true" />{CARRIER_PREF_META[pid].label}</li>)}
                        </ul>
                      : <p className="ap-muted">Non précisé</p>}
                  </section>
                </>
              )}
            </div>

            {/* Ce qu'en disent les membres : les deux derniers avis rédigés */}
            {avisRediges.length > 0 && (
              <section className="ap-section" aria-labelledby="ap-avis" data-testid="ap-avis">
                <h2 id="ap-avis" className="ap-h2">Ce qu'en disent les {isTrajet ? 'expéditeurs' : 'transporteurs'}</h2>
                <div className="ap-grid">
                  {avisRediges.map((r) => (
                    <figure key={r.id} className="ap-avis">
                      <span className="ap-avis-note" aria-label={`${r.score} sur 5`}>{'★'.repeat(r.score)}<span aria-hidden="true">{'★'.repeat(5 - r.score)}</span></span>
                      <blockquote>« {r.comment.trim()} »</blockquote>
                      <figcaption>
                        <MemberAvatar firstName={r.author?.first_name || ''} lastName={r.author?.last_name || ''} size={34} />
                        {r.author?.first_name} {r.author?.last_name ? `${r.author.last_name}.` : ''} · {new Date(r.created_at).toLocaleDateString('fr-FR')}
                      </figcaption>
                    </figure>
                  ))}
                </div>
                {avis.total > avisRediges.length && (
                  <button type="button" className="ap-btn-link ap-avis-tout" onClick={() => navigate('/app/profile/' + a.user.id)}>
                    Lire les {avis.total} avis
                  </button>
                )}
              </section>
            )}

            {/* Photos (colis) */}
            {!isTrajet && (() => {
              const photos = Array.isArray(a.raw?.photos) ? a.raw.photos : [];
              return (
                <section className="ap-section" aria-labelledby="ap-photos">
                  <h2 id="ap-photos" className="ap-h3">{photos.length > 0 ? `Photos (${photos.length})` : 'Photos'}</h2>
                  {photos.length > 0
                    ? <div className="dp-photos ap-photos">{photos.map((p, i) => <img key={i} className="dp-photo-thumb dp-photo-thumb--zoom" src={p} alt={`photo-${i + 1}`} loading="lazy" onClick={() => setLightboxSrc(p)} />)}</div>
                    : <p className="ap-muted">Colis sans photo</p>}
                </section>
              );
            })()}
          </main>

          {/* ══════ SIDEBAR ══════ */}
          <aside className="dp-sidebar">

            {/* Carte actions — thème sombre */}
            <div id="ap-action-card" className="dp-card dp-card-pad dp-action-card ap-action-card">

              {/* ── Sélecteur listing top (sender + carrier) ── */}
              {user && !isOwner && hasRole && (
                <div className="dp-sidebar-colis-top">
                  <ListingChooser
                    entries={ranked}
                    selected={topSelected}
                    selectedId={selectedId}
                    onSelect={chooseListing}
                    meSide={meSide}
                    loading={loadingMine}
                    eta={meSide === 'sender' ? topTripDays : null}
                    createHref={meSide === 'sender' ? '/app/shipments/new' : '/app/trips/new'}
                  />
                  <div className="dp-sidebar-colis-divider" />
                </div>
              )}

              {/* Hero : prix + rôle */}
              <div className="dp-price-row">
                <div className="dp-price-left">
                  <span className="dp-price-display">
                    {isNoPrice ? 'Budget libre' : `${a.price} ${a.unit}`}
                  </span>
                  <span className="dp-price-sub">
                    {isTrajet
                      ? (a.raw?.pricing_mode === 'fixe' ? 'Prix fixe · forfait' : a.raw?.pricing_mode === 'libre' ? 'Prix libre' : 'Tarif · par kg')
                      : (isNoPrice ? 'Prix à proposer' : 'Budget proposé')}
                  </span>
                </div>
                <span className="dp-role-badge">
                  En tant qu'{meSide === 'sender' ? 'expéditeur' : 'transporteur'}
                </span>
              </div>

              {/* Choix unique (direction C, option A du 27/10) : comment procéder — les trois niveaux de proposition */}
              <div role="radiogroup" aria-label="Comment voulez-vous procéder ?" className="ap-choices">
                {meSide === 'sender' && (
                  <button type="button" role="radio" aria-checked={activeTab === 'sequestre'} data-testid="dp-tab-sequestre"
                    className={`ap-choice${activeTab === 'sequestre' ? ' active' : ''}${user && (isOwner || !hasRole || isLibre) ? ' dp-tab--dim' : ''}`}
                    onClick={() => setActiveTab('sequestre')}>
                    <span className="ap-choice-title"><Lock size={15} aria-hidden="true" />Réserver maintenant</span>
                    <span className="ap-choice-sub">Votre carte n'est débitée que s'il accepte.</span>
                  </button>
                )}
                <button type="button" role="radio" aria-checked={activeTab === 'direct'} data-testid="dp-tab-direct"
                  className={`ap-choice${activeTab === 'direct' ? ' active' : ''}${user && (isOwner || !hasRole || isLibre || isNoPrice) ? ' dp-tab--dim' : ''}`}
                  onClick={() => setActiveTab('direct')}>
                  <span className="ap-choice-title"><Send size={15} aria-hidden="true" />{meSide === 'sender' ? "Lui proposer d'abord" : 'Accepter son budget'}</span>
                  <span className="ap-choice-sub">
                    {isLibre || isNoPrice
                      ? (meSide === 'sender' ? 'Pas de prix fixé : négociez-le.' : 'Pas de budget fixé : proposez votre prix.')
                      : (meSide === 'sender' ? 'Sans payer maintenant, réponse sous 12 h.' : 'Son prix, réponse sous 12 h.')}
                  </span>
                </button>
                <button type="button" role="radio" aria-checked={activeTab === 'negocier'} data-testid="dp-tab-negocier"
                  className={`ap-choice${activeTab === 'negocier' ? ' active' : ''}${user && (isOwner || !hasRole) ? ' dp-tab--dim' : ''}`}
                  onClick={() => setActiveTab('negocier')}>
                  <span className="ap-choice-title"><ArrowLeftRight size={15} aria-hidden="true" />{meSide === 'sender' ? 'Négocier le prix' : 'Proposer mon prix'}</span>
                  <span className="ap-choice-sub">
                    {!isNegotiable && isFirm ? 'Impossible : il a fixé son prix.' : 'Votre prix, réponse sous 3 jours.'}
                  </span>
                </button>
              </div>

              {/* ── Si non connecté : démo + CTA login ── */}
              {!user && (
                <>
                  {activeTab === 'sequestre' ? (
                    isLibre
                      ? <div className="dp-info-note">Ce trajet n'a pas de prix fixé : négociez-le avec le transporteur.</div>
                      : <TabDemoEscrow
                          annonceUnit={a.unit}
                          annoncePricePerKg={a.raw?.price_per_kg}
                          annoncePriceFixed={a.raw?.price_fixed}
                          pricingMode={a.raw?.pricing_mode || 'kg'}
                        />
                  ) : <TabDemo
                    activeTab={activeTab}
                    isTrajet={isTrajet}
                    isNoPrice={isNoPrice}
                    annoncePrice={a.price}
                    annonceUnit={a.unit}
                    annonceCcy={annonceBaseCcy}
                    annonceFrom={a.from}
                    annonceTo={a.to}
                    pricingMode={a.raw?.pricing_mode || 'kg'}
                    annoncePricePerKg={a.raw?.price_per_kg}
                    annoncePriceFixed={a.raw?.price_fixed}
                  />}
                  <div className="dp-login-prompt">
                    <p className="dp-login-prompt-title">Prêt à faire une proposition ?</p>
                    <p className="dp-login-prompt-sub">Créez un compte gratuit ou connectez-vous pour interagir.</p>
                    <div className="dp-login-actions">
                      <button
                        className="dp-login-btn-primary"
                        onClick={() => {
                          const r = encodeURIComponent(`/annonces/${type}/${id}`);
                          window.location.href = `/register?return=${r}`;
                        }}
                      >
                        <span>S'inscrire</span>
                        <span className="dp-login-btn-sub">gratuit · 2 min</span>
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        className="dp-login-btn-secondary"
                        data-testid="dp-login-btn"
                        onClick={() => {
                          const r = encodeURIComponent(`/annonces/${type}/${id}`);
                          window.location.href = `/login?return=${r}`;
                        }}
                      >
                        Se connecter
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Propriétaire ── */}
              {user && isOwner && (
                <div className="dp-role-banner dp-role-banner--neutral" data-testid="dp-owner-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>Vous êtes l'auteur de cette annonce. Vous ne pouvez pas vous proposer à vous-même ; vous pouvez seulement <strong>signaler</strong> un problème.</span>
                </div>
              )}

              {/* ── Rôle manquant ── */}
              {user && !isOwner && !hasRole && (
                <div className="dp-role-banner dp-role-banner--warn" data-testid="dp-role-banner-warn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <span>
                    Pour proposer ou réserver, il vous faut le rôle <strong>{ROLE_LABELS[needsRole]}</strong>.{' '}
                    <Link to={roleRequestPath(needsRole, location.pathname)} data-testid="dp-request-role">Demander le rôle {ROLE_LABELS[needsRole]}</Link>
                    {' '}· Vous pouvez quand même écrire à l'annonceur.
                  </span>
                </div>
              )}

              {/* ── Identité non vérifiée : proposer et contacter bloqués ── */}
              {user && !isOwner && !kycOk && activeTab !== 'signaler' && (
                <VerificationGate user={user} action={activeTab === 'contacter' ? 'contact' : activeTab === 'sequestre' ? 'reserve' : 'propose'} variant="panel" />
              )}

              {/* ── Panel : Propose direct ── */}
              {user && !isOwner && hasRole && kycOk && (
                <div className={`dp-panel${activeTab === 'direct' ? ' active' : ''}`} id="panel-direct" data-testid="dp-panel-direct">
                  {isLibre || isNoPrice
                    ? <button type="button" className="dp-libre-cta" data-testid={isLibre ? 'dp-note-libre' : 'dp-note-no-price'}
                        onClick={() => setActiveTab('negocier')}>
                        <span className="dp-libre-cta__icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        </span>
                        <span className="dp-libre-cta__body">
                          <span className="dp-libre-cta__title">
                            {isLibre ? 'Tarif libre, offre personnalisée' : 'Budget non défini, proposez votre tarif'}
                          </span>
                          <span className="dp-libre-cta__sub">
                            Choisissez <strong>Négocier le prix</strong> pour faire votre offre
                          </span>
                        </span>
                        <span className="dp-libre-cta__arrow">→</span>
                      </button>
                    : (
                      <>
                        {/* Ordre des panneaux : devise, montant, frais (expéditeur) ou commission (transporteur), puis le reste */}
                        {/* Mon annonce se choisit en haut de la carte (ListingChooser) : rien de répété ici */}
                        {!selectedId && myListings.length > 0 && (
                          <div className="dp-hint" data-testid="dp-choose-listing-hint">
                            {meSide === 'sender' ? 'Choisissez votre colis en haut de la carte.' : 'Choisissez votre trajet en haut de la carte.'}
                          </div>
                        )}

                        {/* Devise */}
                        {selectedId && (
                          <div className="dp-currency-field">
                            {!currencyCompatible && (
                              <div className="dp-warning-note" style={{ marginBottom: 8 }}>
                                ⚠️ Vos devises déclarées ne correspondent pas à celles de cette annonce. Vous pouvez quand même envoyer une proposition.
                              </div>
                            )}
                            <span className="dp-section-label">Devise de la proposition</span>
                            <CurrencySelector
                              myCurrencies={myListingCurrencies}
                              theirCurrencies={listingCurrencies}
                              theirPrimary={a?.raw?.primary_currency}
                              theirFirstName={a?.raw?.author?.first_name || a?.raw?.owner?.first_name || ''}
                              amountCcy={a?.raw?.primary_currency || 'EUR'}
                              amount={targetPrice?.amount || 0}
                              value={currency}
                              onChange={setCurrency}
                              onConvert={draft.onTargetConverted}
                            />
                          </div>
                        )}

                        <div className="dp-calc-box">
                          {priceRecapRows}
                        </div>

                        {/* Frais Colis2Ship de l'expéditeur (C8) — offerts au lancement */}
                        {meSide === 'sender' && <SenderFeeSummary fee={draft.senderFee('direct')} totalLabel="Total à payer s'il accepte" withTotal />}
                        {meSide === 'carrier' && (
                          <CommissionPreview
                            amount={draft.directPrice}
                            currency={currency || 'EUR'}
                          />
                        )}

                        <div>
                          <span className="dp-section-label">Message (optionnel)</span>
                          <div className="dp-textarea-wrap">
                            <textarea className="dp-textarea" value={message} onChange={e => setMessage(e.target.value)} maxLength={300}
                              placeholder={`Bonjour ${a.user.name.split(' ')[0]}, je suis intéressé(e) par votre annonce…`} />
                            <span className="dp-char-count">{message.length}/300</span>
                          </div>
                        </div>
                        {draft.floorNote('direct') && selectedId && (
                          <p className="dp-hint" style={{color:'#b45309',marginBottom:'4px'}} data-testid="dp-price-floor-direct">
                            {draft.floorNote('direct')}
                          </p>
                        )}
                        {conversionMissing && selectedId && (
                          <p className="dp-hint" style={{color:'#b45309',marginBottom:'4px'}} data-testid="dp-conversion-missing">
                            Conversion indisponible pour le moment : choisissez la devise de l'annonce, ou passez en négociation.
                          </p>
                        )}
                        {alreadyProposed && (
                          <p className="dp-hint" style={{color:'#dc2626',marginBottom:'4px'}}>
                            Vous avez déjà une proposition en attente pour ce colis.
                          </p>
                        )}
                        <button className="dp-cta dp-cta--submit" data-testid="dp-btn-submit-direct" onClick={() => submitProposal('direct')}
                          disabled={submitting || myListings.length === 0 || !selectedId || alreadyProposed || conversionMissing || draft.belowFloor('direct')}>
                          {alreadyProposed ? '✓ Proposition en attente' : submitting ? '⏳ Envoi…' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>Envoyer ma proposition</>}
                        </button>
                        <div className="dp-hint">Prix repris depuis l'annonce. Aucun paiement prélevé à cette étape.</div>
                      </>
                    )
                  }
                </div>
              )}

              {/* ── Panel : Négocier ── */}
              {user && !isOwner && hasRole && kycOk && (
                <div className={`dp-panel${activeTab === 'negocier' ? ' active' : ''}`} id="panel-negocier" data-testid="dp-panel-neg">
                  {!isNegotiable && isFirm
                    ? <div className="dp-info-note" data-testid="dp-note-ferme">🔒 Cette annonce est à prix ferme. La négociation n'est pas acceptée.</div>
                    : (
                      <>
                        {/* Mon annonce se choisit en haut de la carte (ListingChooser) : rien de répété ici */}
                        {!selectedId && myListings.length > 0 && (
                          <div className="dp-hint" data-testid="dp-choose-listing-hint">
                            {meSide === 'sender' ? 'Choisissez votre colis en haut de la carte.' : 'Choisissez votre trajet en haut de la carte.'}
                          </div>
                        )}
                        {/* Devise — partagée avec l'onglet Propose direct */}
                        {selectedId && (
                          <div className="dp-currency-field">
                            {!currencyCompatible && (
                              <div className="dp-warning-note" style={{ marginBottom: 8 }}>
                                ⚠️ Vos devises déclarées ne correspondent pas à celles de cette annonce. Vous pouvez quand même envoyer une proposition.
                              </div>
                            )}
                            <span className="dp-section-label">Devise de la contre-offre</span>
                            <CurrencySelector
                              myCurrencies={myListingCurrencies}
                              theirCurrencies={listingCurrencies}
                              theirPrimary={a?.raw?.primary_currency}
                              theirFirstName={a?.raw?.author?.first_name || a?.raw?.owner?.first_name || ''}
                              amountCcy={a?.raw?.primary_currency || 'EUR'}
                              amount={targetPrice?.amount || 0}
                              value={currency}
                              onChange={setCurrency}
                              onConvert={draft.onTargetConverted}
                            />
                          </div>
                        )}
                        <div>
                          <span className="dp-section-label">Votre prix proposé ({ccySymCurrency(currency)})</span>
                          <div className="dp-price-input">
                            <span>{ccySymCurrency(currency)}</span>
                            <input type="number" min="0" step="1" data-testid="dp-price-neg" value={price} onChange={e => setPrice(e.target.value)} placeholder="Ex. 30" />
                          </div>
                        </div>
                        {negDiff && price && <span className={`dp-diff ${negDiff.cls}`}>{negDiff.label}</span>}
                        {draft.floorNote('negotiation') && (
                          <p className="dp-hint" style={{color:'#b45309',marginBottom:'4px'}} data-testid="dp-price-floor-neg">
                            {draft.floorNote('negotiation')}
                          </p>
                        )}
                        {meSide === 'sender' && <SenderFeeSummary fee={draft.senderFee('negotiation')} totalLabel="Total à payer s'il accepte" withTotal />}
                        {meSide === 'carrier' && (
                          <CommissionPreview amount={price ? Number(price) : null} currency={currency || 'EUR'} />
                        )}
                        <div>
                          <span className="dp-section-label">Message</span>
                          <div className="dp-textarea-wrap">
                            <textarea className="dp-textarea" value={message} onChange={e => setMessage(e.target.value)} maxLength={300}
                              placeholder="Expliquez votre contre-offre…" />
                            <span className="dp-char-count">{message.length}/300</span>
                          </div>
                        </div>
                        <button className="dp-cta dp-cta--submit" data-testid="dp-btn-submit-neg" onClick={() => submitProposal('negotiation')}
                          disabled={submitting || myListings.length === 0 || !selectedId || draft.priceMissing || draft.belowFloor('negotiation')}>
                          {submitting ? '⏳ Envoi…' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>Envoyer ma négociation</>}
                        </button>
                      </>
                    )
                  }
                </div>
              )}

              {/* ── Panel : Contacter ── */}
              {user && !isOwner && kycOk && (
                <div className={`dp-panel${activeTab === 'contacter' ? ' active' : ''}`} id="panel-contacter" data-testid="dp-panel-contacter">
                  <div className="dp-contact-user">
                    <MemberAvatar firstName={author.first_name || a.user.name} lastName={author.last_name || ''} size={36} />
                    <div>
                      <div className="dp-contact-user-name">{a.user.name}</div>
                      <div className="dp-contact-user-meta">★ {a.user.score} · {a.user.trips} avis</div>
                    </div>
                  </div>
                  <div className="dp-info-note">💬 Posez une question avant de vous engager. Aucune offre n'est envoyée à cette étape.</div>
                  <div>
                    <span className="dp-section-label">Votre message</span>
                    <div className="dp-textarea-wrap">
                      <textarea className="dp-textarea" data-testid="dp-contact-msg" value={contactMsg} onChange={e => setContactMsg(e.target.value)} maxLength={500}
                        placeholder={`Bonjour ${a.user.name.split(' ')[0]}, j'ai une question concernant votre annonce…`} />
                      <span className="dp-char-count">{contactMsg.length}/500</span>
                    </div>
                  </div>
                  <button className="dp-cta dp-cta--submit" data-testid="dp-btn-submit-contacter" onClick={submitContact} disabled={submitting || !contactMsg.trim()}>
                    {submitting ? '⏳ Envoi…' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>Envoyer le message</>}
                  </button>
                </div>
              )}

              {/* ── Panel : Signaler ── */}
              {user && (
                <div className={`dp-panel${activeTab === 'signaler' ? ' active' : ''}`} id="panel-signaler" data-testid="dp-panel-signaler">
                  <div className="dp-warning-note" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px' }}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                    <span>Un signalement est examiné par notre équipe de modération sous 24h.</span>
                  </div>
                  <div>
                    <span className="dp-section-label">Motif</span>
                    <div className="dp-demo-radio-list">
                      {REPORT_REASONS.map(opt => (
                        <div
                          key={opt.value}
                          className={`dp-demo-radio-item${reportReason === opt.value ? ' active' : ''}`}
                          onClick={() => setReportReason(opt.value)}
                          style={{ cursor: 'pointer' }}>
                          <div className={`dp-demo-radio dp-demo-radio--signaler${reportReason === opt.value ? ' on' : ''}`} />
                          <span>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="dp-section-label">Détails</span>
                    <div className="dp-textarea-wrap">
                      <textarea className="dp-textarea" value={reportDetails} onChange={e => setReportDetails(e.target.value)} maxLength={500} placeholder="Décrivez la situation…" />
                      <span className="dp-char-count">{reportDetails.length}/500</span>
                    </div>
                  </div>
                  <button className="dp-cta dp-cta--red" onClick={submitReport} disabled={submitting || !reportDetails.trim()}>
                    {submitting ? '⏳ Envoi…' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>Envoyer le signalement</>}
                  </button>
                </div>
              )}

              {/* ── Panel : Réserver (séquestre, expéditeur) ── */}
              {activeTab === 'sequestre' && user && !isOwner && hasRole && kycOk && canPay && !escrowSuccess && (
                !selectedId
                  ? <div className="dp-hint">Choisissez d'abord le colis à confier, ci-dessus.</div>
                  : !ccyStripeOk(currency || a.raw?.primary_currency || 'EUR')
                    ? <div className="dp-info-note" data-testid="dp-escrow-not-payable">
                        La carte ne permet pas de payer en {currency || a.raw?.primary_currency} : choisissez une autre devise pour réserver avec paiement sécurisé.
                      </div>
                    : <>
                        {/* Le séquestre suit la devise choisie (D24) : même sélecteur que sous « Proposer » et « Négocier » */}
                        <div className="dp-currency-field" data-testid="dp-escrow-currency">
                          <span className="dp-section-label">Devise du paiement</span>
                          <CurrencySelector
                            myCurrencies={myListingCurrencies}
                            theirCurrencies={listingCurrencies}
                            theirPrimary={a?.raw?.primary_currency}
                            theirFirstName={author.first_name || ''}
                            amountCcy={a?.raw?.primary_currency || 'EUR'}
                            amount={targetPrice?.amount || 0}
                            value={currency}
                            onChange={setCurrency}
                            onConvert={draft.onTargetConverted}
                          />
                        </div>
                        <EscrowPanel
                          a={a}
                          feeSummary={<SenderFeeSummary fee={draft.senderFee('escrow')} totalLabel="Total à réserver" withTotal />}
                          recapRows={priceRecapRows}
                          price={draft.escrowPrice}
                          tripPrice={draft.targetPrice?.amount}
                          currency={currency}
                          conversionMissing={draft.escrowConversionMissing}
                          selectedId={selectedId}
                          onSuccess={() => { setEscrowSuccess(true); toast.success('Paiement réservé avec succès. Le transporteur a été notifié.'); }}
                        />
                        <div className="dp-secure-note">S'il refuse, rien n'est débité. Paiement par Stripe.</div>
                      </>
              )}

              {/* Signaler : lien discret, ouvre le panneau dans la carte */}
              {activeTab !== 'signaler' && (
                <button type="button" data-testid="dp-tab-signaler" className="ap-report-link" onClick={() => setActiveTab('signaler')}>
                  <Flag size={13} aria-hidden="true" /> Signaler l'annonce
                </button>
              )}

            </div>


            {/* Pour colis : note séquestre côté transporteur — visible uniquement sur les onglets d'action */}
            {!isTrajet && user && !isOwner && hasRole && kycOk && (activeTab === 'direct' || activeTab === 'negocier') && (
              <div className="dp-info-note">
                🔒 <span>Si l'expéditeur accepte votre proposition, il sécurisera le paiement de son côté. Vous n'avez rien à faire ici.</span>
              </div>
            )}

            {/* Trust timeline — processus séquestre illustré, adapté au rôle */}
            <div className="dp-trust-timeline">
              <div className="dp-tl-step">
                <div className="dp-tl-dot dp-tl-dot--done">✓</div>
                <div className="dp-tl-content">
                  <div className="dp-tl-label">{meSide === 'sender' ? 'Vous réservez' : 'Vous proposez'}</div>
                  <div className="dp-tl-sub">{meSide === 'sender' ? 'Poids + message au transporteur' : 'Votre trajet + message à l\'expéditeur'}</div>
                </div>
              </div>
              <div className="dp-tl-step">
                <div className="dp-tl-dot dp-tl-dot--done">✓</div>
                <div className="dp-tl-content">
                  <div className="dp-tl-label">{meSide === 'sender' ? 'Paiement sécurisé Stripe' : 'Paiement sécurisé expéditeur'}</div>
                  <div className="dp-tl-sub">{meSide === 'sender' ? 'Fonds sécurisés, transporteur notifié' : 'Il sécurise le paiement, rien à faire de votre côté'}</div>
                  <span className="dp-tl-tag dp-tl-tag--accent">{meSide === 'sender' ? 'Carte non débitée maintenant' : 'Aucun débit de votre part'}</span>
                </div>
              </div>
              <div className="dp-tl-step">
                <div className="dp-tl-dot dp-tl-dot--active">→</div>
                <div className="dp-tl-content">
                  <div className="dp-tl-label">{meSide === 'sender' ? 'Accord transporteur' : 'Accord expéditeur'}</div>
                  <div className="dp-tl-sub">{meSide === 'sender' ? 'Il accepte · remise du colis convenue' : 'Il valide · remise du colis convenue'}</div>
                </div>
              </div>
              <div className="dp-tl-step">
                <div className="dp-tl-dot dp-tl-dot--pending">○</div>
                <div className="dp-tl-content">
                  <div className="dp-tl-label">Remise + QR scan</div>
                  <div className="dp-tl-sub">Confirmation via code unique</div>
                </div>
              </div>
              <div className="dp-tl-step dp-tl-step--last">
                <div className="dp-tl-dot dp-tl-dot--pending">€</div>
                <div className="dp-tl-content">
                  <div className="dp-tl-label">Paiement libéré</div>
                  <div className="dp-tl-sub">Automatique à la livraison confirmée</div>
                  <span className="dp-tl-tag dp-tl-tag--success">Litige possible pendant 48 h</span>
                </div>
              </div>
              <div className="dp-tl-footer">🔒 Paiement 100% sécurisé via <strong>Stripe</strong></div>
            </div>

            {/* Partager — panel enrichi */}
            <div className="dp-share-panel">
              <button className="dp-share-row" onClick={() => { setShareOpen(o => !o); setShowQr(false); }}>
                <span className="dp-share-ico">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                </span>
                <span>Partager l'annonce</span>
                <span className="dp-share-chev">{shareOpen ? '▲' : '▼'}</span>
              </button>
              {shareOpen && (
                <div className="dp-share-options">
                  <button className="dp-share-opt" onClick={handleWAShare}>
                    <span className="dp-share-opt-ico">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                        <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                    <span>WhatsApp</span>
                  </button>
                  <button className="dp-share-opt" onClick={() => setShowQr(o => !o)}>
                    <span className="dp-share-opt-ico">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
                        <rect x="5" y="5" width="3" height="3" rx=".5" fill="currentColor" stroke="none"/><rect x="16" y="5" width="3" height="3" rx=".5" fill="currentColor" stroke="none"/><rect x="5" y="16" width="3" height="3" rx=".5" fill="currentColor" stroke="none"/>
                        <path d="M14 14h2v2h-2zM16 14h2M16 16v2M14 18h2v2M18 18h2M18 16h2v2"/>
                      </svg>
                    </span>
                    <span>QR Code</span>
                  </button>
                  <button className="dp-share-opt" onClick={handleCopyLink}>
                    <span className="dp-share-opt-ico">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                      </svg>
                    </span>
                    <span>Copier lien</span>
                  </button>
                </div>
              )}
              {shareOpen && showQr && (
                <div className="dp-share-qr">
                  <QRCodeSVG value={window.location.href} size={140} />
                  <div className="dp-share-qr-hint">Scanner pour accéder à l'annonce</div>
                </div>
              )}
            </div>

          </aside>
        </div>
      </div>

      <OnboardingModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        returnPath={location.pathname}
      />

    </div>
  );
}
